import React, { useState, useEffect } from 'react';
import './InsightsDashboard.css';
import { getETDateString } from './utils/marketUtils';
import { isActualEntry } from './utils/tradeUtils';
import { sortSuccessPatterns, sortAvoidPatterns, capitalizeCategory, capitalizeTier } from './utils/patternSort';
import { getProfile as apiGetProfile, listReviews as apiListReviews, getEnrichedTrades as apiGetEnriched, getSimulatedResultsRange as apiGetSimRange } from './services/apiService';
import { AI_SOURCE } from './constants/ai';

/**
 * Trading Insights Dashboard
 * Displays key trading insights and review history
 */
function InsightsDashboard({ onClose, onReviewNow }) {
  const [insights, setInsights] = useState(null);
  const [lastReview, setLastReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);
  const [showAllWinning, setShowAllWinning] = useState(false);
  const [showHealthDetails, setShowHealthDetails] = useState(true);
  const [coverageGaps, setCoverageGaps] = useState(null);
  const [healthSort, setHealthSort] = useState({ key: 'type', dir: 'asc' });

  // Load data on mount
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load latest insights from API
      const profileResp = await apiGetProfile();
      let latestInsights = null;
      if (profileResp?.profileData) {
        latestInsights = typeof profileResp.profileData === 'string' ? JSON.parse(profileResp.profileData) : profileResp.profileData;
      }
      if (profileResp?.coverage && profileResp.coverage !== 'null') {
        const cov = typeof profileResp.coverage === 'string' ? JSON.parse(profileResp.coverage) : profileResp.coverage;
        if (!latestInsights) latestInsights = {};
        latestInsights.coverage = cov;
      }
      setInsights(latestInsights);

      // Load latest review info from API
      const reviewsResp = await apiListReviews();
      const reviews = Array.isArray(reviewsResp) ? reviewsResp : [];
      if (reviews.length > 0) {
        // Reviews come sorted by date desc from API
        const latest = reviews[0];
        setLastReview({
          date: latest.reviewDate,
          timeRange: latest.timeRange,
        });
      } else {
        setLastReview(null);
      }

      setLoading(false);

      // Async: detect coverage gaps using API data (non-blocking)
      try {
        const coverage = latestInsights?.coverage;
        if (coverage?.reviewRanges?.length > 0) {
          const today = getETDateString();
          const gaps = await detectCoverageGapsFromApi(coverage.reviewRanges, coverage.end || coverage.start, today);
          if (gaps.length > 0) {
            const totalTrades = gaps.reduce((sum, g) => sum + (g.tradeCount || 0), 0);
            const totalSim = gaps.reduce((sum, g) => sum + (g.simCount || 0), 0);
            const totalSimEntered = gaps.reduce((sum, g) => sum + (g.simEnteredCount || 0), 0);
            const totalDays = gaps.reduce((sum, g) => sum + (g.dateCount || 1), 0);
            setCoverageGaps({ gaps, totalTrades, totalSim, totalSimEntered, totalDays, start: gaps[0].start, end: gaps[gaps.length - 1].end });
          }
        }
      } catch (gapErr) {
        console.log('Gap detection skipped:', gapErr.message);
      }
    } catch (err) {
      console.error('Error loading insights:', err);
      setError('Failed to load insights: ' + err.message);
      setLoading(false);
    }
  };

  // Detect coverage gaps using API data instead of file system
  const detectCoverageGapsFromApi = async (reviewRanges, startDate, endDate) => {
    const [enrichedResp, simResp] = await Promise.all([
      apiGetEnriched(startDate, endDate).catch(() => null),
      apiGetSimRange(startDate, endDate).catch(() => null),
    ]);

    // Build a map of dates with trade counts
    const dateMap = new Map();

    // Enriched trades
    if (Array.isArray(enrichedResp)) {
      enrichedResp.forEach(item => {
        const date = item.tradeDate;
        const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
        const count = data?.enrichedPairs?.length || 1;
        if (dateMap.has(date)) {
          dateMap.get(date).tradeCount += count;
        } else {
          dateMap.set(date, { date, tradeCount: count, simCount: 0, simEnteredCount: 0 });
        }
      });
    }

    // Simulated results
    if (Array.isArray(simResp)) {
      simResp.forEach(item => {
        const date = item.simDate;
        const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
        const trades = Array.isArray(data?.trades) ? data.trades : [];
        const count = trades.length || 1;
        const entered = trades.filter(isActualEntry).length;
        if (dateMap.has(date)) {
          dateMap.get(date).simCount += count;
          dateMap.get(date).simEnteredCount += entered;
        } else {
          dateMap.set(date, { date, tradeCount: 0, simCount: count, simEnteredCount: entered });
        }
      });
    }

    // Filter out dates that are covered by existing reviews
    const allDates = [...dateMap.values()].sort((a, b) => a.date.localeCompare(b.date));
    const uncovered = allDates.filter(item =>
      !reviewRanges.some(r => item.date >= r.start && item.date <= r.end)
    );

    // Merge consecutive dates into gap ranges
    if (uncovered.length === 0) return [];
    const gaps = [];
    let current = { start: uncovered[0].date, end: uncovered[0].date, tradeCount: uncovered[0].tradeCount, simCount: uncovered[0].simCount, simEnteredCount: uncovered[0].simEnteredCount || 0, dateCount: 1 };

    for (let i = 1; i < uncovered.length; i++) {
      const prev = new Date(current.end);
      const curr = new Date(uncovered[i].date);
      const diffDays = Math.round((curr - prev) / (86400000));

      if (diffDays <= 3) { // Allow weekends
        current.end = uncovered[i].date;
        current.tradeCount += uncovered[i].tradeCount;
        current.simCount += uncovered[i].simCount;
        current.simEnteredCount += uncovered[i].simEnteredCount || 0;
        current.dateCount++;
      } else {
        gaps.push(current);
        current = { start: uncovered[i].date, end: uncovered[i].date, tradeCount: uncovered[i].tradeCount, simCount: uncovered[i].simCount, simEnteredCount: uncovered[i].simEnteredCount || 0, dateCount: 1 };
      }
    }
    gaps.push(current);
    return gaps;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get time range display name
  const getTimeRangeLabel = (timeRange) => {
    const labels = {
      'day': 'Daily',
      'daily': 'Daily',
      'week': 'Weekly',
      'weekly': 'Weekly',
      'month': 'Monthly',
      'monthly': 'Monthly',
      'all': 'All Time'
    };
    return labels[timeRange] || timeRange;
  };

  /**
   * Calculate Preferred AI from successfulPatterns using weighted scoring
   * Score = Σ (trades × winRate × profitBonus) + patternBonus
   * profitBonus = 1 + (avgProfit / 1000)
   * patternBonus = patternCount × 50
   */
  const calculatePreferredAI = (patterns) => {
    if (!patterns || patterns.length === 0) {
      return { preferred: 'N/A', claudeScore: 0, gptScore: 0, breakdown: [], claudePatternCount: 0, gptPatternCount: 0 };
    }

    let claudeScore = 0;
    let gptScore = 0;
    let claudePatternCount = 0;
    let gptPatternCount = 0;
    const breakdown = [];

    patterns.forEach(p => {
      const trades = p.trades || 0;
      const winRate = p.winRate || 0;
      const avgProfit = p.avgProfit || 0;

      // Calculate profit bonus: 1 + (avgProfit / 1000)
      // e.g., $2000 profit -> 1 + 2 = 3x multiplier
      const profitBonus = 1 + (avgProfit / 1000);

      // Base score = trades × winRate × profitBonus
      const baseScore = trades * winRate * profitBonus;

      const source = (p.source || '').toLowerCase();

      // Build breakdown entry
      const breakdownEntry = {
        pattern: p.pattern || 'Unknown',
        trades,
        winRate,
        avgProfit,
        profitBonus: Math.round(profitBonus * 100) / 100,
        baseScore: Math.round(baseScore),
        source: p.source || 'Unknown',
        claudeContribution: 0,
        gptContribution: 0
      };

      if (source === AI_SOURCE.CLAUDE) {
        claudeScore += baseScore;
        claudePatternCount++;
        breakdownEntry.claudeContribution = Math.round(baseScore);
      } else if (source === AI_SOURCE.GPT) {
        gptScore += baseScore;
        gptPatternCount++;
        breakdownEntry.gptContribution = Math.round(baseScore);
      } else if (source === AI_SOURCE.BOTH) {
        // Both AIs identified this pattern, split the score
        claudeScore += baseScore / 2;
        gptScore += baseScore / 2;
        claudePatternCount += 0.5;
        gptPatternCount += 0.5;
        breakdownEntry.claudeContribution = Math.round(baseScore / 2);
        breakdownEntry.gptContribution = Math.round(baseScore / 2);
      }

      breakdown.push(breakdownEntry);
    });

    // Add pattern bonus: each unique pattern adds 50 points
    const claudePatternBonus = Math.round(claudePatternCount * 50);
    const gptPatternBonus = Math.round(gptPatternCount * 50);
    claudeScore += claudePatternBonus;
    gptScore += gptPatternBonus;

    return {
      preferred: claudeScore >= gptScore ? 'Claude' : 'GPT',
      claudeScore: Math.round(claudeScore),
      gptScore: Math.round(gptScore),
      breakdown,
      claudePatternCount,
      gptPatternCount,
      claudePatternBonus,
      gptPatternBonus
    };
  };

  // Render performance cards
  const renderPerformanceCards = () => {
    if (!insights?.userProfile) return null;

    const profile = insights.userProfile;
    const winRate = parseFloat(profile.winRate) || 0;
    const avgProfit = parseFloat(profile.avgProfit) || 0;
    const totalTrades = parseInt(profile.totalTrades) || 0;
    const pairsAnalyzed = parseInt(profile.pairsAnalyzed) || totalTrades;
    const totalProfit = profile.totalProfit != null
      ? parseFloat(profile.totalProfit)
      : avgProfit * pairsAnalyzed;

    // Calculate preferred AI: use backtest win rate (aiRecommendationAccuracy) if available, fallback to pattern scores
    const accuracy = profile.aiRecommendationAccuracy;
    const aiPreference = accuracy?.claudeWinRate != null && accuracy?.gptWinRate != null
      ? {
          ...calculatePreferredAI(profile.successfulPatterns),
          preferred: accuracy.claudeWinRate >= accuracy.gptWinRate ? 'Claude' : 'GPT'
        }
      : calculatePreferredAI(profile.successfulPatterns);

    return (
      <div className="performance-cards">
        <div className="perf-card">
          <div className="perf-value">{profile.winRate}%</div>
          <div className="perf-label">Win Rate</div>
        </div>
        <div className="perf-card">
          <div className="perf-value">{pairsAnalyzed !== totalTrades ? `${pairsAnalyzed}/${totalTrades}` : totalTrades}</div>
          <div className="perf-label">Total Trades</div>
        </div>
        <div className="perf-card">
          <div className={`perf-value ${totalProfit >= 0 ? 'positive' : 'negative'}`}>
            ${totalProfit >= 0 ? '+' : ''}{totalProfit.toFixed(0)}
            <span className="pl-avg"> (avg ${avgProfit >= 0 ? '+' : ''}{avgProfit})</span>
          </div>
          <div className="perf-label">P/L</div>
        </div>
        <div className="perf-card" title={`Claude: ${aiPreference.claudeScore} | GPT: ${aiPreference.gptScore}`}>
          <div className={`perf-value ${aiPreference.preferred === 'Claude' ? 'claude-color' : 'gpt-color'}`}>
            {aiPreference.preferred}
          </div>
          <div className="perf-label">Preferred AI</div>
        </div>
      </div>
    );
  };

  // Render AI Comparison Section (Ranking List)
  const renderAIComparison = () => {
    if (!insights?.userProfile?.successfulPatterns) return null;

    const aiPreference = calculatePreferredAI(insights.userProfile.successfulPatterns);
    const accuracy = insights.userProfile.aiRecommendationAccuracy;
    const totalScore = aiPreference.claudeScore + aiPreference.gptScore;

    // Build AI providers array for ranking (extensible for future AIs)
    const aiProviders = [
      {
        id: AI_SOURCE.CLAUDE,
        name: 'Claude',
        icon: 'C',
        color: '#7c3aed',
        score: aiPreference.claudeScore,
        winRate: accuracy?.claudeWinRate,
        patternCount: aiPreference.claudePatternCount,
        patternBonus: aiPreference.claudePatternBonus
      },
      {
        id: AI_SOURCE.GPT,
        name: 'GPT',
        icon: 'G',
        color: '#16a34a',
        score: aiPreference.gptScore,
        winRate: accuracy?.gptWinRate,
        patternCount: aiPreference.gptPatternCount,
        patternBonus: aiPreference.gptPatternBonus
      }
      // Future: Add Gemini, Grok, etc.
    ];

    // Sort by backtest win rate if available, fallback to score
    const rankedProviders = accuracy?.claudeWinRate != null && accuracy?.gptWinRate != null
      ? [...aiProviders].sort((a, b) => (b.winRate || 0) - (a.winRate || 0))
      : [...aiProviders].sort((a, b) => b.score - a.score);
    const maxScore = rankedProviders[0]?.score || 1;

    // Truncate pattern name for display
    const truncatePattern = (pattern, maxLen = 40) => {
      if (!pattern) return 'Unknown';
      return pattern.length > maxLen ? pattern.substring(0, maxLen) + '...' : pattern;
    };

    return (
      <section className="section ai-ranking-section">
        <div className="section-header">
          <h3>AI Provider Ranking</h3>
          <button
            className="btn-toggle-breakdown"
            onClick={() => setShowScoreBreakdown(!showScoreBreakdown)}
          >
            {showScoreBreakdown ? '▲ Hide Details' : '▼ Show Details'}
          </button>
        </div>

        {/* Ranking List */}
        <div className="ai-ranking-list">
          {rankedProviders.map((provider, idx) => {
            const hasWinRate = provider.winRate != null;
            const percent = hasWinRate
              ? Math.round(provider.winRate)
              : (totalScore > 0 ? Math.round((provider.score / totalScore) * 100) : 0);
            const barWidth = hasWinRate ? provider.winRate : percent;
            const isPreferred = idx === 0;

            return (
              <div key={provider.id} className={`ai-ranking-row ${provider.id}`}>
                <span className="rank-check">{isPreferred ? '✓' : ''}</span>
                <span className="rank-number">{idx + 1}.</span>
                <span className="rank-icon" style={{ background: provider.color }}>{provider.icon}</span>
                <span className="rank-name" style={{ color: provider.color }}>{provider.name}</span>
                <div className="rank-bar-container">
                  <div
                    className="rank-bar"
                    style={{ width: `${barWidth}%`, background: provider.color }}
                  />
                </div>
                <span className="rank-score">{hasWinRate ? `${provider.winRate}%` : provider.score.toLocaleString()}</span>
                <span className="rank-percent">{hasWinRate ? 'Win Rate' : `(${percent}%)`}</span>
              </div>
            );
          })}
        </div>

        {/* Score Breakdown (Expandable) */}
        {showScoreBreakdown && (
          <div className="score-breakdown">
            <div className="breakdown-header">
              <span className="col-pattern">Pattern</span>
              <span className="col-trades">Trades</span>
              <span className="col-winrate">Win%</span>
              <span className="col-profit">Avg Profit</span>
              <span className="col-bonus">Bonus</span>
              {rankedProviders.map(p => (
                <span key={p.id} className={`col-ai col-${p.id}`}>{p.name}</span>
              ))}
            </div>

            {aiPreference.breakdown.map((item, idx) => (
              <div key={idx} className={`breakdown-row source-${item.source.toLowerCase()}`}>
                <span className="col-pattern" title={item.pattern}>
                  {truncatePattern(item.pattern)}
                  <span className={`source-tag ${item.source.toLowerCase()}`}>{item.source}</span>
                </span>
                <span className="col-trades">{item.trades}</span>
                <span className="col-winrate">{item.winRate}%</span>
                <span className="col-profit">${item.avgProfit?.toFixed(0) || 0}</span>
                <span className="col-bonus">x{item.profitBonus}</span>
                <span className="col-ai col-claude">{item.claudeContribution > 0 ? `+${item.claudeContribution.toLocaleString()}` : '-'}</span>
                <span className="col-ai col-gpt">{item.gptContribution > 0 ? `+${item.gptContribution.toLocaleString()}` : '-'}</span>
              </div>
            ))}

            {/* Pattern Bonus Row */}
            <div className="breakdown-row bonus-row">
              <span className="col-pattern">Pattern Bonus (patterns × 50)</span>
              <span className="col-trades">-</span>
              <span className="col-winrate">-</span>
              <span className="col-profit">-</span>
              <span className="col-bonus">-</span>
              {rankedProviders.map(p => (
                <span key={p.id} className={`col-ai col-${p.id}`}>+{p.patternBonus}</span>
              ))}
            </div>

            {/* Total Row */}
            <div className="breakdown-row total-row">
              <span className="col-pattern"><strong>Total Score</strong></span>
              <span className="col-trades">-</span>
              <span className="col-winrate">-</span>
              <span className="col-profit">-</span>
              <span className="col-bonus">-</span>
              {rankedProviders.map(p => (
                <span key={p.id} className={`col-ai col-${p.id}`}><strong>{p.score.toLocaleString()}</strong></span>
              ))}
            </div>

            {/* Formula Note */}
            <div className="formula-note">
              <strong>Formula:</strong> Score = Σ (trades × winRate × (1 + avgProfit/1000)) + patternCount × 50
            </div>
          </div>
        )}
      </section>
    );
  };

  // Render Best Market Conditions (When to trade)
  const renderMarketConditions = () => {
    if (!insights?.userProfile?.bestMarketConditions) return null;

    const conditions = insights.userProfile.bestMarketConditions;

    // Check if we have any conditions to display
    const hasPositive = conditions.bestSentiment || conditions.bestIndexCondition || conditions.scoreThreshold;
    const hasNegative = conditions.worstSentiment;

    if (!hasPositive && !hasNegative) return null;

    return (
      <section className="section market-conditions-section">
        <h3>Best Market Conditions</h3>
        <div className="market-conditions-columns">
          {/* Left: Positive Conditions (Green) */}
          <div className="conditions-column positive">
            <div className="column-header">Trade When</div>
            <div className="conditions-list">
              {conditions.bestSentiment && (
                <div className="condition-item">
                  <span className="condition-badge">+</span>
                  <span className="condition-text">Sentiment: {conditions.bestSentiment}</span>
                </div>
              )}
              {conditions.bestIndexCondition && (
                <div className="condition-item">
                  <span className="condition-badge">+</span>
                  <span className="condition-text">{conditions.bestIndexCondition}</span>
                </div>
              )}
              {conditions.scoreThreshold && (
                <div className="condition-item">
                  <span className="condition-badge">+</span>
                  <span className="condition-text">{conditions.scoreThreshold}</span>
                </div>
              )}
            </div>
          </div>

          {/* Right: Negative Conditions (Red) */}
          <div className="conditions-column negative">
            <div className="column-header">Avoid When</div>
            <div className="conditions-list">
              {conditions.worstSentiment && (
                <div className="condition-item">
                  <span className="condition-badge">x</span>
                  <span className="condition-text">Sentiment: {conditions.worstSentiment}</span>
                </div>
              )}
              {!conditions.worstSentiment && (
                <div className="condition-item empty">
                  <span className="condition-text">No specific conditions identified</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  };

  // Get status icon based on pattern type and _meta
  const getPatternIcon = (pattern, isAvoid = false) => {
    if (isAvoid) return '\u{1F6AB}';
    const meta = pattern._meta || {};
    const trend = meta.trend || 'stable';
    if (trend === 'new') return '\u{1F195}';
    if (trend === 'improving') return '\u{1F4C8}';
    if (trend === 'declining' || (meta.missedReviews || 0) >= 2) return '\u26A0\uFE0F';
    return '\u2705';  // stable + healthy
  };

  // Format short date for health details
  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Sort comparator for health details table
  const sortHealthPatterns = (patterns, sortConfig) => {
    const trendOrder = { improving: 0, declining: 1, new: 2, stable: 3 };
    const typeOrder = { Win: 0, Avoid: 1 };

    return [...patterns].sort((a, b) => {
      const dir = sortConfig.dir === 'asc' ? 1 : -1;

      switch (sortConfig.key) {
        case 'type': {
          const typeA = typeOrder[a._type] ?? 1;
          const typeB = typeOrder[b._type] ?? 1;
          if (typeA !== typeB) return (typeA - typeB) * dir;
          const trendA = trendOrder[a._meta?.trend || 'stable'] ?? 3;
          const trendB = trendOrder[b._meta?.trend || 'stable'] ?? 3;
          if (trendA !== trendB) return trendA - trendB;
          return (b._meta?.reviewCount || 1) - (a._meta?.reviewCount || 1);
        }
        case 'pattern':
          return dir * (a.pattern || '').localeCompare(b.pattern || '');
        case 'trend': {
          const trendA = (a._meta?.missedReviews || 0) >= 2 ? 1 : (trendOrder[a._meta?.trend || 'stable'] ?? 3);
          const trendB = (b._meta?.missedReviews || 0) >= 2 ? 1 : (trendOrder[b._meta?.trend || 'stable'] ?? 3);
          if (trendA !== trendB) return (trendA - trendB) * dir;
          return (b._meta?.reviewCount || 1) - (a._meta?.reviewCount || 1);
        }
        case 'reviews':
          return dir * ((a._meta?.reviewCount || 1) - (b._meta?.reviewCount || 1));
        case 'missed':
          return dir * ((a._meta?.missedReviews || 0) - (b._meta?.missedReviews || 0));
        case 'since': {
          const dateA = a._meta?.firstSeen ? new Date(a._meta.firstSeen).getTime() : 0;
          const dateB = b._meta?.firstSeen ? new Date(b._meta.firstSeen).getTime() : 0;
          return dir * (dateA - dateB);
        }
        default:
          return 0;
      }
    });
  };

  // Toggle sort column
  const handleHealthSort = (key) => {
    setHealthSort(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort indicator
  const getSortIndicator = (key) => {
    if (healthSort.key !== key) return '';
    return healthSort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  // Render Winning Patterns (What works)
  const renderWinningPatterns = () => {
    const patterns = insights?.userProfile?.successfulPatterns || [];

    // Truncate pattern name
    const truncatePattern = (pattern, maxLen = 60) => {
      if (!pattern) return 'Unknown';
      return pattern.length > maxLen ? pattern.substring(0, maxLen) + '...' : pattern;
    };

    // Sort: trades desc, winRate desc tiebreaker (best wins first when tied)
    // Shared with AiReviewDialog so both views show patterns in the same order.
    const sorted = sortSuccessPatterns(patterns);

    const DEFAULT_SHOW = 6;
    const displayed = showAllWinning ? sorted : sorted.slice(0, DEFAULT_SHOW);
    const hasMore = sorted.length > DEFAULT_SHOW;

    return (
      <div className="patterns-column winning">
        <div className="column-header">What Works</div>
        <div className="patterns-list">
          {displayed.length > 0 ? (
            <>
              {displayed.map((p, idx) => (
                <div key={idx} className="pattern-card" title={p.pattern}>
                  {/* Tier (always) + Category (when AI has classified) badges */}
                  <div className="pattern-badges">
                    <span className={`tier-badge tier-${p.tier || 'emerging'}`}>
                      {capitalizeTier(p.tier)}
                    </span>
                    {p.category && (
                      <span className={`cat-badge cat-${p.category}`}>
                        {capitalizeCategory(p.category)}
                      </span>
                    )}
                  </div>
                  <span className="pattern-icon">{getPatternIcon(p)}</span>
                  <div className="pattern-content">
                    <div className="pattern-name">{truncatePattern(p.pattern)}</div>
                    <div className="pattern-stats">
                      <span className="stat-winrate">{p.winRate}% win rate</span>
                      {p.trades && <span className="stat-trades">{p.trades} trades</span>}
                      {p.avgProfit && <span className="stat-profit">avg +${p.avgProfit.toFixed(0)}</span>}
                      {p.source && <span className={`source-tag ${p.source.toLowerCase()}`}>{p.source}</span>}
                      {p.dataOrigin && p.dataOrigin !== 'real' && (
                        <span className={`origin-tag ${p.dataOrigin}`}>
                          {p.dataOrigin === 'simulated' ? 'Sim' : 'Mixed'}
                        </span>
                      )}
                    </div>
                    {p._meta && (
                      <div className="pattern-health">
                        <span className={`health-trend trend-${p._meta.trend || 'stable'}`}>
                          {p._meta.trend === 'improving' && '\u2197 '}
                          {p._meta.trend === 'declining' && '\u2198 '}
                          {p._meta.trend === 'new' && ''}
                          {p._meta.trend === 'stable' && '\u2192 '}
                          {p._meta.trend || 'stable'}
                        </span>
                        <span className="health-reviews">{p._meta.reviewCount || 1} review{(p._meta.reviewCount || 1) !== 1 ? 's' : ''}</span>
                        {(p._meta.missedReviews || 0) > 0 && (
                          <span className="health-missed">missed {p._meta.missedReviews}</span>
                        )}
                      </div>
                    )}
                    {p._meta && (
                      <div className="pattern-winrate-bar">
                        <div className="winrate-bar-track">
                          <div
                            className={`winrate-bar-fill trend-${p._meta.trend || 'stable'}`}
                            style={{ width: `${Math.min(p.winRate || 0, 100)}%` }}
                          />
                        </div>
                        <span className="winrate-bar-label">
                          {p._meta.winRateHistory && p._meta.winRateHistory.length >= 2
                            ? `${p._meta.winRateHistory[0]}\u2192${p.winRate}%`
                            : `${p.winRate}%`
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {hasMore && (
                <button
                  className="show-all-btn"
                  onClick={() => setShowAllWinning(!showAllWinning)}
                >
                  {showAllWinning ? 'Show less' : `Show all (${sorted.length})`}
                </button>
              )}
            </>
          ) : (
            <div className="pattern-card empty">
              <span className="empty-text">No winning patterns identified yet</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Patterns to Avoid (What's dangerous)
  const renderAvoidPatterns = () => {
    const patterns = insights?.userProfile?.avoidPatterns || [];

    // Truncate pattern name
    const truncatePattern = (pattern, maxLen = 60) => {
      if (!pattern) return 'Unknown';
      return pattern.length > maxLen ? pattern.substring(0, maxLen) + '...' : pattern;
    };

    // Sort: trades desc, winRate asc tiebreaker (worst losses first when tied)
    // Shared with AiReviewDialog so both views show patterns in the same order.
    const sorted = sortAvoidPatterns(patterns);

    return (
      <div className="patterns-column danger">
        <div className="column-header">What to Avoid</div>
        <div className="patterns-list">
          {sorted.length > 0 ? (
            sorted.map((p, idx) => (
              <div key={idx} className="pattern-card" title={p.pattern}>
                {/* Tier (always) + Category (when AI has classified) badges */}
                <div className="pattern-badges">
                  <span className={`tier-badge tier-${p.tier || 'emerging'}`}>
                    {capitalizeTier(p.tier)}
                  </span>
                  {p.category && (
                    <span className={`cat-badge cat-${p.category}`}>
                      {capitalizeCategory(p.category)}
                    </span>
                  )}
                </div>
                <span className="pattern-icon">{getPatternIcon(p, true)}</span>
                <div className="pattern-content">
                  <div className="pattern-name">{truncatePattern(p.pattern)}</div>
                  <div className="pattern-stats">
                    <span className="stat-winrate">{p.winRate}% win rate</span>
                    {p.trades && <span className="stat-trades">{p.trades} trades</span>}
                    {p.avgLoss && <span className="stat-loss">avg loss ${Math.abs(p.avgLoss).toFixed(0)}</span>}
                    {p.source && <span className={`source-tag ${p.source.toLowerCase()}`}>{p.source}</span>}
                    {p.dataOrigin && p.dataOrigin !== 'real' && (
                      <span className={`origin-tag ${p.dataOrigin}`}>
                        {p.dataOrigin === 'simulated' ? 'Sim' : 'Mixed'}
                      </span>
                    )}
                  </div>
                  {p._meta && (
                    <div className="pattern-health">
                      <span className={`health-trend trend-${p._meta.trend || 'stable'}`}>
                        {p._meta.trend === 'improving' && '\u2197 '}
                        {p._meta.trend === 'declining' && '\u2198 '}
                        {p._meta.trend === 'new' && ''}
                        {p._meta.trend === 'stable' && '\u2192 '}
                        {p._meta.trend || 'stable'}
                      </span>
                      <span className="health-reviews">{p._meta.reviewCount || 1} review{(p._meta.reviewCount || 1) !== 1 ? 's' : ''}</span>
                      {(p._meta.missedReviews || 0) > 0 && (
                        <span className="health-missed">missed {p._meta.missedReviews}</span>
                      )}
                    </div>
                  )}
                  {p._meta && (
                    <div className="pattern-winrate-bar">
                      <div className="winrate-bar-track">
                        <div
                          className={`winrate-bar-fill trend-${p._meta.trend || 'stable'}`}
                          style={{ width: `${Math.min(p.winRate || 0, 100)}%` }}
                        />
                      </div>
                      <span className="winrate-bar-label">
                        {p._meta.winRateHistory && p._meta.winRateHistory.length >= 2
                          ? `${p._meta.winRateHistory[0]}\u2192${p.winRate}%`
                          : `${p.winRate}%`
                        }
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="pattern-card empty">
              <span className="empty-text">No patterns to avoid identified yet</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Health Details table
  const renderHealthDetails = () => {
    const success = insights?.userProfile?.successfulPatterns || [];
    const avoid = insights?.userProfile?.avoidPatterns || [];

    const allPatterns = [
      ...success.map(p => ({ ...p, _type: 'Win' })),
      ...avoid.map(p => ({ ...p, _type: 'Avoid' })),
    ];

    if (allPatterns.length === 0) return null;

    const truncatePattern = (pattern, maxLen = 30) => {
      if (!pattern) return 'Unknown';
      return pattern.length > maxLen ? pattern.substring(0, maxLen) + '...' : pattern;
    };

    const sorted = sortHealthPatterns(allPatterns, healthSort);

    return (
      <div className="health-details-section">
        <button
          className="btn-toggle-health"
          onClick={() => setShowHealthDetails(!showHealthDetails)}
        >
          {showHealthDetails ? '\u25B2 Hide' : '\u25BC'} Pattern Health Details
        </button>

        {showHealthDetails && (
          <div className="health-details-table">
            <div className="health-header">
              <span className="hcol-pattern sortable" onClick={() => handleHealthSort('pattern')}>
                Pattern{getSortIndicator('pattern')}
              </span>
              <span className="hcol-type sortable" onClick={() => handleHealthSort('type')}>
                Type{getSortIndicator('type')}
              </span>
              <span className="hcol-trend sortable" onClick={() => handleHealthSort('trend')}>
                Trend{getSortIndicator('trend')}
              </span>
              <span className="hcol-reviews sortable" onClick={() => handleHealthSort('reviews')}>
                Reviews{getSortIndicator('reviews')}
              </span>
              <span className="hcol-missed sortable" onClick={() => handleHealthSort('missed')}>
                Missed{getSortIndicator('missed')}
              </span>
              <span className="hcol-since sortable" onClick={() => handleHealthSort('since')}>
                Since{getSortIndicator('since')}
              </span>
            </div>
            {sorted.map((p, idx) => {
              const meta = p._meta || {};
              const trend = meta.trend || 'stable';
              return (
                <div key={idx} className={`health-row trend-${trend} ${(meta.missedReviews || 0) >= 2 ? 'has-missed' : ''}`}>
                  <span className="hcol-pattern" title={p.pattern}>
                    {getPatternIcon(p, p._type === 'Avoid')} {truncatePattern(p.pattern, 30)}
                  </span>
                  <span className={`hcol-type type-${p._type.toLowerCase()}`}>{p._type}</span>
                  <span className={`hcol-trend trend-${trend}`}>
                    {trend === 'improving' && '\u2197 '}
                    {trend === 'declining' && '\u2198 '}
                    {trend === 'stable' && '\u2192 '}
                    {trend}
                  </span>
                  <span className="hcol-reviews">{meta.reviewCount || 1}</span>
                  <span className={`hcol-missed ${(meta.missedReviews || 0) > 0 ? 'warn' : ''}`}>
                    {meta.missedReviews || 0}
                  </span>
                  <span className="hcol-since">
                    {meta.firstSeen ? formatShortDate(meta.firstSeen) : '-'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="insights-overlay" onClick={onClose}>
      <div className="insights-dashboard" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="insights-header">
          <h2>AI Insights</h2>
          <button className="close-btn" onClick={onClose}>x</button>
        </div>

        {/* Content */}
        <div className="insights-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading insights...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadData}>Retry</button>
            </div>
          ) : !insights ? (
            <div className="empty-state">
              <div className="empty-icon">?</div>
              <h3>No Insights Yet</h3>
              <p>Generate an AI Review to see your trading insights here.</p>
            </div>
          ) : (
            <>
              {/* Review Reminder */}
              {coverageGaps && (
                <div className="review-reminder warn">
                  <span className="reminder-text">
                    {coverageGaps.totalTrades > 0 && coverageGaps.totalSim > 0
                      ? `${coverageGaps.totalDays} day${coverageGaps.totalDays !== 1 ? 's' : ''} with ${coverageGaps.totalTrades} trades + ${coverageGaps.totalSimEntered}/${coverageGaps.totalSim} simulated unreviewed (${formatDate(coverageGaps.start)} – ${formatDate(coverageGaps.end)})`
                      : coverageGaps.totalTrades > 0
                        ? `${coverageGaps.totalDays} day${coverageGaps.totalDays !== 1 ? 's' : ''} with ${coverageGaps.totalTrades} unreviewed trade${coverageGaps.totalTrades !== 1 ? 's' : ''} (${formatDate(coverageGaps.start)} – ${formatDate(coverageGaps.end)})`
                        : `${coverageGaps.totalDays} day${coverageGaps.totalDays !== 1 ? 's' : ''} with ${coverageGaps.totalSimEntered}/${coverageGaps.totalSim} simulated trades unreviewed (${formatDate(coverageGaps.start)} – ${formatDate(coverageGaps.end)})`
                    }
                  </span>
                  {onReviewNow && (
                    <button
                      className="btn-review-now"
                      onClick={() => onReviewNow({ start: coverageGaps.start, end: coverageGaps.end })}
                    >
                      Review Now
                    </button>
                  )}
                </div>
              )}

              {/* Performance at a Glance */}
              <section className="section performance-section">
                <div className="section-header">
                  <h3>Performance at a Glance</h3>
                  <span className="last-updated">
                    Updated: {insights.lastUpdated ? formatDate(insights.lastUpdated.split('T')[0]) : 'N/A'}
                    {lastReview && (
                      <span className="last-review-info">
                        {' · '}Last review: {formatDate(lastReview.date)} ({getTimeRangeLabel(lastReview.timeRange)})
                      </span>
                    )}
                  </span>
                </div>
                {renderPerformanceCards()}
              </section>

              {/* AI Provider Comparison */}
              {renderAIComparison()}

              {/* Best Market Conditions */}
              {renderMarketConditions()}

              {/* Trading Patterns Section */}
              {(insights?.userProfile?.successfulPatterns?.length > 0 ||
                insights?.userProfile?.avoidPatterns?.length > 0) && (
                <section className="section trading-patterns-section">
                  <h3>Trading Patterns</h3>
                  <div className="patterns-row">
                    {renderWinningPatterns()}
                    {renderAvoidPatterns()}
                  </div>
                  {renderHealthDetails()}
                </section>
              )}

</>
          )}
        </div>

      </div>

    </div>
  );
}

export default InsightsDashboard;
